import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Observable, tap, map, catchError, finalize, share, throwError } from 'rxjs';
import { SessionService } from './session.service';
import { IdleTimeoutService } from './idle-timeout.service';
import { AuthUser, LoginRequest } from '../models/auth.models';
import { AuthApiService } from './auth-api.service';
import { APP_ROUTES, TOAST_KEY, TOAST_LIFE } from '../models/app.constants';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private sessionUserService = inject(SessionService);

    private _user = signal<AuthUser | null>(this.sessionUserService.getUser());
    private _activeCompany = signal<number | null>(this.sessionUserService.getActiveCompanyId());
    private _activeBranch = signal<number | null>(this.sessionUserService.getActiveBranchId());
    private _activeCompanyName = signal<string | null>(this.sessionUserService.getActiveCompanyName());
    private _activeBranchName = signal<string | null>(this.sessionUserService.getActiveBranchName());

    readonly user = this._user.asReadonly();
    readonly isLoggedIn = computed(() => !!this._user());
    readonly fullName = computed(() => this._user()?.fullName ?? '');
    readonly roles = computed(() => this._user()?.roles ?? []);

    readonly isSuperAdmin = computed(() => this.roles().includes('ROLE_SUPER_ADMIN'));
    readonly isAdmin = computed(() => this.isSuperAdmin() || this.roles().includes('ROLE_ADMIN'));

    // Empresa/sucursal EFECTIVA:
    //  - SUPER_ADMIN: la cambia en runtime (null = "Todas").
    //  - ADMIN: empresa fija (la suya); sucursal cambiable dentro de su empresa.
    //  - DOCTOR/SECRETARY: siempre las de su propio usuario (claims del JWT).
    readonly idCompany = computed(() => {
        const user = this._user();
        if (!user) return null;
        if (this.isSuperAdmin()) {
            return this._activeCompany();
        }
        return user.idCompany;
    });

    readonly idBranch = computed(() => {
        const user = this._user();
        if (!user) return null;
        if (this.isSuperAdmin()) {
            return this._activeBranch();
        }
        if (this.roles().includes('ROLE_ADMIN')) {
            return this._activeBranch() ?? user.idBranch;
        }
        return user.idBranch;
    });

    // Nombres visibles (sidebar): para SUPER_ADMIN no se muestran; el resto
    // usa la empresa del usuario y la sucursal seleccionada (o la asignada).
    readonly activeCompanyName = computed(() => {
        const user = this._user();
        if (!user) return '';
        if (this.isSuperAdmin()) {
            return this._activeCompanyName() ?? '';
        }
        return user.companyName;
    });

    readonly activeBranchName = computed(() => {
        const user = this._user();
        if (!user) return '';
        if (this.isSuperAdmin()) {
            return this._activeBranchName() ?? '';
        }
        if (this.roles().includes('ROLE_ADMIN')) {
            return this._activeBranchName() ?? user.branchName;
        }
        return user.branchName;
    });

    constructor(
        private authApiService: AuthApiService,
        private sessionService: SessionService,
        private router: Router,
        private messageService: MessageService,
        private idleTimeout: IdleTimeoutService
    ) {
        // Si ya hay sesión activa (recarga de página), reactivamos el control de inactividad
        if (this.isLoggedIn()) {
            this.idleTimeout.start();
        }
    }

    // Cambio de empresa/sucursal activa. Persiste el tenant y recarga la app
    // para que todos los módulos queden dentro del nuevo tenant.
    // - SUPER_ADMIN: cambia empresa y/o sucursal (null = "Todas").
    // - ADMIN: solo sucursal (la empresa siempre es la suya).
    switchTenant(
        companyId: number | null,
        branchId: number | null,
        companyName?: string | null,
        branchName?: string | null
    ): void {
        this.sessionService.setActiveTenant(companyId, branchId, companyName, branchName);
        this._activeCompany.set(companyId);
        this._activeBranch.set(branchId);
        this._activeCompanyName.set(companyName ?? null);
        this._activeBranchName.set(branchName ?? null);
        window.location.reload();
    }

    // Guard para operaciones de escritura: requieren una empresa concreta.
    // Devuelve true si hay empresa seleccionada; si no, muestra un toast.
    requireTenant(): boolean {
        if (this.idCompany()) {
            return true;
        }
        this.messageService.add({
            key: TOAST_KEY,
            severity: 'warn',
            summary: 'Seleccione una empresa',
            detail: 'Para crear registros debe seleccionar una empresa (y opcionalmente sucursal) en el selector superior.',
            life: TOAST_LIFE
        });
        return false;
    }

    // ─── helpers de sesión ────────────────────────────────────────────────────

    login(request: LoginRequest): Observable<void> {
        return new Observable((observer) => {
            this.authApiService.login(request).subscribe({
                next: (response) => {
                    this.sessionService.saveTokens(response.accessToken, response.refreshToken);
                    this.sessionService.saveUser(response.user);
                    this._user.set(response.user);
                    this._activeCompany.set(null);
                    this._activeBranch.set(null);
                    this._activeCompanyName.set(null);
                    this._activeBranchName.set(null);
                    this.sessionService.clearActiveTenant();
                    this.idleTimeout.start();
                    this.router.navigate([APP_ROUTES.DASHBOARD]);
                    observer.next();
                    observer.complete();
                },
                error: (err) => observer.error(err)
            });
        });
    }

    // Llamado por el authInterceptor cuando recibe un 401 o para renovar
    // proactivamente el access token mientras el usuario está activo.
    // Single-flight: varias peticiones concurrentes comparten la misma renovación.
    private refreshInFlight: Observable<void> | null = null;

    refreshTokens(): Observable<void> {
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        const refreshToken = this.sessionService.getRefreshToken();
        if (!refreshToken) {
            this.clearAndRedirect();
            return throwError(() => new Error('No refresh token'));
        }
        this.refreshInFlight = this.authApiService.refreshToken(refreshToken).pipe(
            tap((response) => {
                this.sessionService.saveTokens(response.accessToken, response.refreshToken);
                this.sessionService.saveUser(response.user);
                this._user.set(response.user);
            }),
            map(() => undefined),
            catchError((err) => {
                this.clearAndRedirect();
                return throwError(() => err);
            }),
            finalize(() => (this.refreshInFlight = null)),
            share()
        );
        return this.refreshInFlight;
    }

    logout(): void {
        const refreshToken = this.sessionService.getRefreshToken();
        this.authApiService.logout(refreshToken).subscribe({
            complete: () => this.clearAndRedirect(),
            error: () => this.clearAndRedirect()
        });
    }

    updateUser(data: Partial<AuthUser>): void {
        const current = this._user();
        if (current) {
            const merged = { ...current, ...data };
            this._user.set(merged);
            this.sessionService.saveUser(merged);
        }
    }

    hasRole(role: string): boolean {
        return this.roles().includes(role);
    }

    hasAnyRole(roles: string[]): boolean {
        return roles.some((r) => this.roles().includes(r));
    }

    private clearAndRedirect(): void {
        this.idleTimeout.stop();
        this.sessionService.clearSession();
        this._user.set(null);
        this._activeCompany.set(null);
        this._activeBranch.set(null);
        this._activeCompanyName.set(null);
        this._activeBranchName.set(null);
        this.router.navigate([APP_ROUTES.AUTH.LOGIN]);
    }
}
